// Every Firestore access for the จองวันส่งสินค้า flow lives here.
// The page must never touch `db` directly.
const { db } = require('@/firebase')
const R = require('./bookingRules')
// Propose and confirm: a booking with a pending proposal is decided on the
// panel, not moved or cancelled past it. See proposalRules.js.
const X = require('./proposalRules')
const V = require('./visitRules')
const U = require('./upcountry')
const RJ = require('./returnJob')
const { locationArea } = require('./serviceArea')
// Whether a booking is the caller's. Checked against every email the account
// has had (userProfile.previousEmails) -- a booking keeps the email it was made
// with, and moving the account to a user ID must not orphan it.
const { ownedBy } = require('./people')
function ownersOf (o) { return (o.owners && o.owners.length) ? o.owners : [o.user] }

// Up-country (large truck) orders live in this collection but are not seated on
// any day: their date is null until a trip is fixed, then a truck's, not a small
// car's. Every small-car write refuses one rather than charging bookingDay/null.
function refuseUpcountry (b) {
  if (U.isUpcountry(b)) throw new R.BookingError('UPCOUNTRY_PATH')
}

const CONFIG_REF = () => db.collection('bookingConfig').doc('default')
const DAY_REF = date => db.collection('bookingDay').doc(date)
const SLOT_COL = () => db.collection('bookingSlot')
const TRIP_COL = () => db.collection('outOfAreaTrip')

// `outOfArea` rides along with the four slot counters but is never one of them:
// see countsAfterAdd in bookingRules for why a far-province trip is counted
// without being charged a seat.
const EMPTY_COUNTS = { am: 0, pm: 0, fullDay: 0, total: 0, outOfArea: 0 }

const BATCH_LIMIT = 450

// `cars` is coalesced rather than trusted: a hand-edited config document
// missing it would make capacityForCars(undefined) return all-zero caps, which
// reads as "every day is full, everywhere" with no diagnostic. `!= null` keeps
// a deliberate 0 (a closed fleet) intact.
async function getConfig () {
  const snap = await CONFIG_REF().get()
  if (!snap.exists) {
    return { cars: R.DEFAULT_CARS, perCar: R.PER_CAR, leadDays: R.LEAD_WORKING_DAYS, horizonDays: R.BOOKING_HORIZON_DAYS }
  }
  const cfg = snap.data()
  return Object.assign({}, cfg, {
    cars: cfg.cars != null ? cfg.cars : R.DEFAULT_CARS,
    // normalizePerCar fills any key the stored document is missing, so a config
    // written before the rate was configurable still yields a complete rate.
    perCar: R.normalizePerCar(cfg.perCar),
    leadDays: R.normalizeLeadDays(cfg.leadDays),
    // A config written before the horizon was configurable has no horizonDays,
    // and normalizeHorizonDays reads that absence as the 60 it always was. So
    // the setting arrives without a migration and without changing a thing
    // until someone deliberately saves a different number.
    horizonDays: R.normalizeHorizonDays(cfg.horizonDays)
  })
}

// A day with no override falls back to the standing fleet size and the standing
// rate. A day edited through the range editor carries a snapshot of BOTH, so
// later changes to the standard leave already-planned days as they were set.
//
// The maths itself lives in bookingRules so check-booking-rules can assert it --
// in particular that a truck held by an out-of-area trip comes out of the day's
// ordinary capacity. Kept as a function here because five call sites and one
// view import it by this name.
function capsForDay (dayDoc, configCars, configPerCar) {
  return R.capacityForDay(dayDoc, configCars, configPerCar)
}

// One listener covers the visible month: ~30 documents regardless of how many
// bookings exist. Days with no document simply do not appear in `byDate`.
function subscribeMonth (startDate, endDate, cb, onError) {
  return db.collection('bookingDay')
    .where('date', '>=', startDate)
    .where('date', '<=', endDate)
    .onSnapshot(snap => {
      const byDate = {}
      snap.forEach(d => { byDate[d.id] = d.data() })
      cb(byDate)
    }, onError)
}

// Opening a route. Two documents, one transaction: the trip itself, and the day
// it takes a truck from.
//
// The day document is not bookkeeping here -- it is the enforcement. A
// transaction cannot run a query, so the only way to refuse a second ราชบุรี
// route on the 15th is for the 15th to know it already has one. checkTripOpen
// is that decision and is asserted in check-booking-rules; this is plumbing.
//
// getConfig is read OUTSIDE the transaction for the same reason createBooking
// reads it outside: the Web SDK v1 requires every document read inside one to
// also be written in it, and the standing fleet size is not written here.
async function openTrip ({ date, provinces, capacity, cars, user }) {
  const cfg = await getConfig()
  const tripRef = TRIP_COL().doc()
  const stamp = new Date().toISOString()

  await db.runTransaction(async t => {
    const daySnap = await t.get(DAY_REF(date))
    const day = daySnap.exists ? daySnap.data() : Object.assign({}, EMPTY_COUNTS)

    const check = R.checkTripOpen(day, { provinces, capacity, cars }, cfg.cars, date)
    if (!check.ok) throw new R.BookingError(check.reason)

    const trip = {
      date: date,
      provinces: check.provinces,
      capacity: check.capacity,
      cars: check.cars,
      count: 0,
      status: R.TRIP_OPEN,
      // Set only by tripAfterSeat. A route closed by filling reopens when a
      // seat comes back; one closed by a person does not.
      autoClosed: false,
      openedBy: user || '',
      openedAt: stamp,
      history: [{ action: 'open', provinces: check.provinces, capacity: check.capacity,
        cars: check.cars, by: user || '', at: stamp }]
    }
    t.set(tripRef, trip)

    // The mirror, and the truck. Both on the day document, both in this
    // transaction, because a route that exists without its truck reserved is a
    // day that will happily fill with Bangkok work and have nothing to send.
    const entry = R.tripEntryOf(tripRef.id, trip)
    const byProvince = Object.assign({}, day.tripByProvince || {})
    check.provinces.forEach(p => { byProvince[p] = entry })
    t.set(DAY_REF(date), {
      date: date,
      carsReserved: R.reservedCars(day) + check.cars,
      tripByProvince: byProvince
    }, { merge: true })
  })

  return tripRef.id
}

// Closing by hand. Distinct from the auto-close in tripAfterSeat: this one
// survives a cancellation, because "we are leaving with three" is a decision.
// The truck stays reserved -- the route is still going.
async function closeTrip (tripId, { user, reason } = {}) {
  await setTripStatus(tripId, R.TRIP_CLOSED, false, { user: user, reason: reason, action: 'close' })
}

// Reopening one that was closed by hand, or closed by filling and then emptied
// by a cancellation that the operator wants to fill again.
async function reopenTrip (tripId, { user } = {}) {
  await setTripStatus(tripId, R.TRIP_OPEN, false, { user: user, action: 'reopen' })
}

async function setTripStatus (tripId, status, autoClosed, log) {
  const tripRef = TRIP_COL().doc(tripId)
  const stamp = new Date().toISOString()
  await db.runTransaction(async t => {
    const tripSnap = await t.get(tripRef)
    if (!tripSnap.exists) throw new R.BookingError('NO_TRIP')
    const trip = tripSnap.data()

    // Inside the transaction, not in the board: the board only stops OFFERING
    // the button, and cancelTrip already gave the truck back -- reopening is a
    // status change that cannot take one again, so a cancelled route coming
    // back would be open, offered to shops, and running on a truck the day has
    // already re-let. Checked before the day is read, because a refusal here
    // must leave nothing read-but-unwritten for the Web SDK to reject.
    if (status === R.TRIP_OPEN) {
      const reopen = R.checkReopen(trip)
      if (!reopen.ok) throw new R.BookingError(reopen.reason)
    }

    const daySnap = await t.get(DAY_REF(trip.date))
    const day = daySnap.exists ? daySnap.data() : Object.assign({}, EMPTY_COUNTS)

    const next = Object.assign({}, trip, { status: status, autoClosed: autoClosed })
    t.update(tripRef, {
      status: status,
      autoClosed: autoClosed,
      closedBy: status === R.TRIP_CLOSED ? (log.user || '') : '',
      closedAt: status === R.TRIP_CLOSED ? stamp : '',
      history: (trip.history || []).concat([{
        action: log.action, reason: log.reason || '', by: log.user || '', at: stamp
      }])
    })
    t.set(DAY_REF(trip.date),
      Object.assign({ date: trip.date }, applyTripDelta(t, day, tripRef, tripId, next)),
      { merge: true })
  })
}

// Capacity is delivery's judgement about the truck and the drive, so it is
// editable after the fact. Lowering it below what is already booked is allowed
// and closes the route -- the same way the จำนวนรถ editor lets a day be cut
// below its bookings and flags it, rather than refusing a real decision.
async function setTripCapacity (tripId, { capacity, user } = {}) {
  const cap = Math.floor(Number(capacity))
  if (!isFinite(cap) || cap < 1) throw new R.BookingError('BAD_CAPACITY')

  const tripRef = TRIP_COL().doc(tripId)
  const stamp = new Date().toISOString()
  await db.runTransaction(async t => {
    const tripSnap = await t.get(tripRef)
    if (!tripSnap.exists) throw new R.BookingError('NO_TRIP')
    const trip = tripSnap.data()

    const daySnap = await t.get(DAY_REF(trip.date))
    const day = daySnap.exists ? daySnap.data() : Object.assign({}, EMPTY_COUNTS)

    const next = R.tripAfterCapacity(trip, cap)
    t.update(tripRef, {
      capacity: cap,
      status: next.status,
      autoClosed: next.autoClosed,
      history: (trip.history || []).concat([{
        action: 'capacity', from: trip.capacity || 0, to: cap, by: user || '', at: stamp
      }])
    })
    t.set(DAY_REF(trip.date),
      Object.assign({ date: trip.date }, applyTripDelta(t, day, tripRef, tripId, next)),
      { merge: true })
  })
}

// Extending a route to another province (กาญจนบุรี on the way to ราชบุรี). No
// truck is taken and no seat changes -- it is the same drive, so carsReserved is
// not touched, and capacity stays แก้จำนวนงาน's decision. What changes is the
// trip's province list and the day's mirror, together, because the mirror is
// what offers the new province to shops: a route that lists กาญจนบุรี without
// its entry on bookingDay would be one nobody could book onto.
//
// Both documents read are written, as the Web SDK v1 requires of a transaction,
// and a refusal throws before any write is queued.
async function addTripProvinces (tripId, { provinces, user } = {}) {
  const tripRef = TRIP_COL().doc(tripId)
  const stamp = new Date().toISOString()
  let added = []
  await db.runTransaction(async t => {
    const tripSnap = await t.get(tripRef)
    if (!tripSnap.exists) throw new R.BookingError('NO_TRIP')
    const trip = tripSnap.data()

    const daySnap = await t.get(DAY_REF(trip.date))
    const day = daySnap.exists ? daySnap.data() : Object.assign({}, EMPTY_COUNTS)

    const check = R.checkTripAddProvinces(day, tripId, trip, provinces)
    if (!check.ok) throw new R.BookingError(check.reason)
    added = check.added

    const next = Object.assign({}, trip, { provinces: check.provinces })
    t.update(tripRef, {
      provinces: check.provinces,
      history: (trip.history || []).concat([{
        action: 'addProvince', provinces: check.added, by: user || '', at: stamp
      }])
    })
    t.set(DAY_REF(trip.date), {
      date: trip.date,
      tripByProvince: R.mirrorAfterTrip(day, tripId, next)
    }, { merge: true })
  })
  return added
}

// Taking a province back off a route. checkTripRemoveProvince refuses while any
// active booking on the route is in it.
//
// Those bookings are found by QUERY, which a transaction cannot run, so the
// query runs inside the callback but outside the transaction, before the trip
// is read. The trip's `count` is the check on it: every seat moves it in a
// transaction of its own, so a booking that lands after the query leaves the
// count above what the query saw and the rule refuses TRIP_STALE; one landing
// after the trip is read changes the trip and makes this transaction retry,
// which re-runs the query. By date and filtered in memory -- the same
// automatic-index rule sendTripToDispatch follows.
//
// The removed province is tombstoned on the day, not deleted: see
// mirrorAfterCancel, which also leaves alone a province another route has
// since taken. No truck and no seat moves.
async function removeTripProvince (tripId, { province, user } = {}) {
  const tripRef = TRIP_COL().doc(tripId)
  const stamp = new Date().toISOString()
  await db.runTransaction(async t => {
    const first = await tripRef.get()
    if (!first.exists) throw new R.BookingError('NO_TRIP')
    const snap = await SLOT_COL().where('date', '==', first.data().date).get()
    const bookings = []
    snap.forEach(d => { if (d.data().tripId === tripId) bookings.push(d.data()) })

    const tripSnap = await t.get(tripRef)
    if (!tripSnap.exists) throw new R.BookingError('NO_TRIP')
    const trip = tripSnap.data()

    const daySnap = await t.get(DAY_REF(trip.date))
    const day = daySnap.exists ? daySnap.data() : Object.assign({}, EMPTY_COUNTS)

    const check = R.checkTripRemoveProvince(trip, province, bookings)
    if (!check.ok) {
      const err = new R.BookingError(check.reason)
      err.count = check.count
      throw err
    }

    t.update(tripRef, {
      provinces: check.provinces,
      history: (trip.history || []).concat([{
        action: 'removeProvince', provinces: [String(province).trim()], by: user || '', at: stamp
      }])
    })
    t.set(DAY_REF(trip.date), {
      date: trip.date,
      tripByProvince: R.mirrorAfterCancel(day, tripId, { provinces: [String(province).trim()] })
    }, { merge: true })
  })
}

// The undo for a route opened by mistake. Refuses one that has bookings: those
// customers were promised a date, and taking the truck back out from under them
// is the one outcome this whole feature exists to prevent. Empty it first by
// cancelling or moving them, then cancel the route.
//
// This is the ONLY path that gives a truck back. closeTrip deliberately keeps
// it -- a closed route is still running.
async function cancelTrip (tripId, { user } = {}) {
  const tripRef = TRIP_COL().doc(tripId)
  const stamp = new Date().toISOString()
  await db.runTransaction(async t => {
    const tripSnap = await t.get(tripRef)
    if (!tripSnap.exists) throw new R.BookingError('NO_TRIP')
    const trip = tripSnap.data()
    if ((trip.count || 0) > 0) throw new R.BookingError('TRIP_HAS_BOOKINGS')

    const daySnap = await t.get(DAY_REF(trip.date))
    const day = daySnap.exists ? daySnap.data() : Object.assign({}, EMPTY_COUNTS)

    t.update(tripRef, {
      status: R.TRIP_CLOSED,
      cancelled: true,
      cancelledBy: user || '',
      cancelledAt: stamp,
      history: (trip.history || []).concat([{ action: 'cancelTrip', by: user || '', at: stamp }])
    })
    t.set(DAY_REF(trip.date), {
      date: trip.date,
      carsReserved: Math.max(0, R.reservedCars(day) - (trip.cars || 1)),
      // A tombstone, not a deleted key: this is a set with merge, and Firestore
      // deep-merges map fields, so an entry dropped from a locally-built map
      // survives untouched in the stored document. See mirrorAfterCancel.
      tripByProvince: R.mirrorAfterCancel(day, tripId, trip)
    }, { merge: true })
    // A route sent as 2 days held its car on the day after too. Written
    // without reading that day: a blind set with merge is allowed, a read the
    // transaction does not write is what the Web SDK v1 rejects.
    if ((Number(trip.days) || 1) > 1) R.routeHoldWrites(tripId, trip, 1).forEach(w => {
      t.set(DAY_REF(w.date), { date: w.date, carriedRoutes: { [tripId]: w.entry } }, { merge: true })
    })
  })
}

// The migration path, and the only one. PR #35 created out-of-area bookings
// before routes existed; they carry outOfArea and no tripId, show on the board
// as ยังไม่มีเส้นทาง, and are attached by a person. There is deliberately no
// backfill script: guessing what counts as one drive is exactly the judgement
// a person should be making.
async function attachToTrip (bookingId, tripId, { user } = {}) {
  const bRef = SLOT_COL().doc(bookingId)
  const tripRef = TRIP_COL().doc(tripId)
  const stamp = new Date().toISOString()

  await db.runTransaction(async t => {
    const bSnap = await t.get(bRef)
    if (!bSnap.exists) throw new R.BookingError('NOT_FOUND')
    const b = bSnap.data()
    refuseUpcountry(b)
    if (!R.isActive(b.status)) throw new R.BookingError('ALREADY_CANCELLED')
    // A pending move already holds a seat on whichever route its own target
    // resolved to; attaching this legacy booking to a different route on top
    // of that double-charges a route for one booking. Decide the proposal
    // first, the same as cancelBooking and moveBooking.
    if (X.isPending(b)) throw new R.BookingError('PROPOSAL_PENDING')
    if (b.tripId) throw new R.BookingError('TRIP_EXISTS')
    // A stray is never linked (visitRules.checkLink wants a route), so this is
    // corrupt data -- refused rather than charging a route for a shared seat.
    if (b.visitId) throw new R.BookingError('VISIT_LINKED')

    const tripSnap = await t.get(tripRef)
    if (!tripSnap.exists) throw new R.BookingError('NO_TRIP')
    const trip = tripSnap.data()

    // The trip's own date, not the booking's. checkAttach has just proved the
    // two are the same, and naming the trip's says which one is authoritative:
    // the mirror being written here is the ROUTE's entry, so a route on another
    // day used to land on the booking's day and offer shops a seat on a truck
    // that was not coming.
    const daySnap = await t.get(DAY_REF(trip.date))
    const day = daySnap.exists ? daySnap.data() : Object.assign({}, EMPTY_COUNTS)

    // Every check an attach must pass, in one rule rather than in the board's
    // route picker -- a view is not a rule, and nothing could assert that one.
    const seat = R.checkAttach(b, trip)
    if (!seat.ok) throw new R.BookingError(seat.reason)

    const next = R.tripAfterSeat(trip)
    t.set(DAY_REF(trip.date),
      Object.assign({ date: trip.date }, applyTripDelta(t, day, tripRef, tripId, next)),
      { merge: true })
    t.update(bRef, {
      tripId: tripId,
      history: (b.history || []).concat([{
        action: 'attachTrip', tripId: tripId, by: user || '', at: stamp
      }])
    })
  })
}

// Every trip in a date range, live. Range filter on `date` alone -- the same
// automatic-index rule subscribeRange follows, and the reason this needs no
// composite index. Status is not filtered: the board shows closed routes too.
function subscribeTrips (from, to, cb, onError) {
  return TRIP_COL()
    .where('date', '>=', from)
    .where('date', '<=', to)
    .onSnapshot(snap => {
      const trips = []
      snap.forEach(d => {
        const trip = d.data()
        // Cancelled routes are dropped here rather than on the board. A route
        // can only be cancelled while it is empty, so a cancelled one carries
        // nothing anyone needs while planning -- and left on the board it read
        // as live: the fill chip says "0/5, plenty of room" in green beside one
        // small grey chip saying otherwise. The document stays in Firestore
        // with its history; it just stops occupying the working board.
        //
        // Filtered in memory, not queried: `cancelled == true` beside the date
        // range would mix an equality with a range, which is the shape that
        // needs a composite index. Same rule subscribeRange follows.
        if (trip.cancelled) return
        trips.push(Object.assign({ id: d.id }, trip))
      })
      trips.sort((a, b) => String(a.date).localeCompare(String(b.date)))
      cb(trips)
    }, onError)
}

// Capacity, Sunday and lead-time rules are ALL re-checked inside the
// transaction. The UI is never trusted: a tab left open overnight will offer
// dates that have since become invalid.
// `role` is the caller's, and is an argument rather than a field of `booking`
// because everything in `booking` is stored on the document.
//
// `booking.visitWith` is the id of a live booking this one joins as one stop
// (the page asked, and a person said yes). A joining booking takes no seat --
// the visit already holds one -- so it skips the capacity and route-seat checks
// and moves no counter but the day's `visits` mirror. Every other rule still
// applies. See visitRules.
async function createBooking (booking, orderDate, { role } = {}) {
  refuseUpcountry(booking)
  const { date, slot } = booking
  const newRef = SLOT_COL().doc()
  const leaderId = booking.visitWith || null
  const leaderRef = leaderId ? SLOT_COL().doc(leaderId) : null

  // Re-derived here rather than taken from the form, wherever the location can
  // speak for itself. The page decides what to SHOW and what to ask; this is
  // what the day's counters are moved by, and a form left open while the
  // service area changed must not be able to store a flag its own address
  // contradicts. Only when the location says nothing -- an SO whose free-text
  // address resolved to no single ตำบล leaves `location` empty -- is the
  // caller's verdict, made from resolveFromAddress and a staff confirmation,
  // the only thing there is to go on.
  const area = locationArea(booking.location)
  const outOfArea = area === 'unknown' ? !!booking.outOfArea : area === 'out'

  // A trip's seat is a different resource from a day's seat, so a booking on a
  // route is checked against the route and not against checkCapacity. The trip
  // document has to be read inside the transaction -- the UI is never trusted,
  // and two shops can want the same last seat.
  // Not read when joining a visit: the visit already holds this route's seat,
  // so the route is not written, and the Web SDK v1 rejects a transaction that
  // reads a document it does not write. checkLink proves the route is the
  // leader's own.
  const tripRef = booking.tripId && !booking.visitWith ? TRIP_COL().doc(booking.tripId) : null

  // Either a seat on an existing route, or the route itself plus its first
  // seat. Never both, and never a second transaction: an openTrip call followed
  // by a separate createBooking can half-fail and strand a reserved truck with
  // nobody on it.
  // Only for a booking this transaction itself judges out of area: an in-area
  // booking handed an openTrip (a stale form, or the view bug that sent one for
  // every staff booking) is booked against the day's capacity, not refused.
  const opening = outOfArea && !booking.tripId && booking.openTrip ? booking.openTrip : null
  const newTripRef = opening ? TRIP_COL().doc() : null
  let openCheck = null

  // The standing fleet size is read OUTSIDE the transaction on purpose. The
  // Web SDK v1 requires every document read inside a transaction to also be
  // WRITTEN in it -- reading bookingConfig without writing it made every commit
  // fail with "Every document read in a transaction must also be written."
  //
  // Atomicity is not lost where it counts: the per-day override and the day's
  // counters, which actually decide whether this booking fits, are still read
  // inside the transaction. Only the rarely-changed fleet default is read
  // ahead of it, and a per-day override takes precedence over it anyway.
  const cfg = await getConfig()

  await db.runTransaction(async t => {
    const daySnap = await t.get(DAY_REF(date))
    const day = daySnap.exists ? daySnap.data() : Object.assign({}, EMPTY_COUNTS)
    const caps = capsForDay(day, cfg.cars, cfg.perCar)

    // Every read before every write: the Web SDK v1 requires it, and both of
    // these documents are written below.
    const tripSnap = tripRef ? await t.get(tripRef) : null
    const trip = tripSnap && tripSnap.exists ? tripSnap.data() : null
    if (tripRef && !trip) throw new R.BookingError('NO_TRIP')
    const leaderSnap = leaderRef ? await t.get(leaderRef) : null
    const leader = leaderSnap && leaderSnap.exists ? leaderSnap.data() : null

    // An unrecognised slot would skip both the AM_FULL and PM_FULL checks yet
    // still consume a seat, so it is rejected before anything is counted.
    if (R.SLOTS.indexOf(slot) === -1) throw new R.BookingError('BAD_SLOT')
    if (R.isClosedSunday(date, day)) throw new R.BookingError('SUNDAY')
    // Nothing else refuses a day that has already happened: TOO_EARLY is
    // measured from the order and TOO_LATE from the horizon, so between them
    // the past was open. Tested before TOO_EARLY so each cause keeps its own
    // message -- "that day has passed" and "your order is too recent" are not
    // the same thing to whoever is reading it.
    if (date < R.todayStr()) throw new R.BookingError('PAST')
    if (date < R.earliestDeliveryDate(orderDate, cfg.leadDays)) throw new R.BookingError('TOO_EARLY')
    if (date > R.horizonDate(R.todayStr(), cfg.horizonDays)) throw new R.BookingError('TOO_LATE')

    // Every rule above still applies to an out-of-area booking -- a route to
    // ราชบุรี is no more deliverable on a Sunday, and the lead time is the
    // shop's promise to the customer either way. What changes is WHICH pool of
    // seats is counted.
    // Before any of the capacity branches, because the bug this catches is one
    // that fell BETWEEN them: out-of-area, no route, so no branch applied.
    const routed = R.checkOutOfAreaBooking(outOfArea, booking.tripId, opening)
    if (!routed.ok) throw new R.BookingError(routed.reason)

    // ปิดรับจอง. Read off the day document already inside this transaction, so
    // a shop whose page loaded before delivery stopped the day is still refused.
    const open = R.checkDayOpen(day, role, { outOfArea: outOfArea })
    if (!open.ok) throw new R.BookingError(open.reason)

    // Joining a visit. Checked against the leader as it is NOW, inside the
    // transaction: it may have been cancelled or moved since the page asked.
    let visitId = null
    let visitPatch = null
    if (leaderRef) {
      if (opening) throw new R.BookingError('VISIT_TRIP')
      const link = V.checkLink(Object.assign({}, booking, { outOfArea, status: 'active' }), null, leader, leaderId)
      if (!link.ok) throw new R.BookingError(link.reason)
      visitId = link.visitId
      // A leader not yet in a visit holds its seat as an ordinary booking; it
      // becomes the visit's first member here, so the join below finds one.
      // At least one either way: the leader is live on this day and slot
      // (checkLink), so a mirror reading zero has drifted.
      const n = leader.visitId ? Math.max(1, V.membersOn(day, visitId)) : 1
      visitPatch = V.join({ visits: { [visitId]: n } }, visitId).visits
      // Written even when it already has a visitId: the Web SDK v1 rejects a
      // transaction that reads a document it does not write.
      t.update(leaderRef, leader.visitId ? { visitId: visitId } : {
        visitId: visitId,
        history: (leader.history || []).concat([{
          action: 'linkVisit', with: booking.soNo || '', by: booking.user || '', at: new Date().toISOString()
        }])
      })
    }

    if (visitId) {
      // No seat checks: the visit already holds the seat this booking shares.
    } else if (tripRef) {
      const seat = R.checkTripSeat(trip)
      if (!seat.ok) throw new R.BookingError(seat.reason)
    } else if (opening) {
      // Same check an explicit openTrip makes, from the same one day document:
      // the province must be unclaimed and the truck must exist.
      const check = R.checkTripOpen(day, opening, cfg.cars, date)
      if (!check.ok) throw new R.BookingError(check.reason)
      openCheck = check
    } else if (!outOfArea) {
      const check = R.checkCapacity(day, caps, slot)
      if (!check.ok) throw new R.BookingError(check.reason)
    }

    const dayPatch = visitId
      ? { date: date, visits: visitPatch }
      : Object.assign({ date: date }, R.countsAfterAdd(day, slot, outOfArea))
    if (tripRef && !visitId) {
      // The trip and its mirror move together. A count on one and not the other
      // is a route that offers a seat it has already sold -- which is the whole
      // reason applyTripDelta exists, so call it rather than repeating it here.
      Object.assign(dayPatch, applyTripDelta(t, day, tripRef, booking.tripId, R.tripAfterSeat(trip)))
    }
    if (opening && openCheck) {
      const stamp = new Date().toISOString()
      const emptyTrip = {
        date: date,
        provinces: openCheck.provinces,
        capacity: openCheck.capacity,
        cars: openCheck.cars,
        count: 0,
        status: R.TRIP_OPEN,
        autoClosed: false,
        openedBy: booking.user || '',
        openedAt: stamp,
        history: [{ action: 'open', provinces: openCheck.provinces,
          capacity: openCheck.capacity, cars: openCheck.cars,
          by: booking.user || '', at: stamp }]
      }
      // The first seat is taken through the same rule every other seat is, so a
      // route whose capacity is 1 closes on opening without that being a special
      // case anyone had to remember to write.
      const trip = R.tripAfterSeat(emptyTrip)
      t.set(newTripRef, trip)
      dayPatch.carsReserved = R.reservedCars(day) + openCheck.cars
      const entry = R.tripEntryOf(newTripRef.id, trip)
      const byProvince = Object.assign({}, day.tripByProvince || {})
      openCheck.provinces.forEach(p => { byProvince[p] = entry })
      dayPatch.tripByProvince = byProvince
    }
    t.set(DAY_REF(date), dayPatch, { merge: true })

    const stored = Object.assign({}, booking)
    delete stored.visitWith
    t.set(newRef, Object.assign(stored, {
      // The visit this booking shares a stop (and a seat) with, or null.
      visitId: visitId,
      // Stored on every booking, not only the out-of-area ones, so `false` is a
      // fact the page can read rather than an absence it has to interpret. The
      // grouping view delivery will use to gather far-province customers reads
      // exactly this field.
      outOfArea: outOfArea,
      // The route this booking sits on. Absent on an ordinary booking, and on
      // an out-of-area booking made before routes existed -- those show on the
      // board as ยังไม่มีเส้นทาง until delivery attaches them.
      tripId: booking.tripId || (newTripRef ? newTripRef.id : null),
      // Active from the moment it is made -- there is no confirm step. See
      // isActive() for why the old 'pending'/'confirmed' records still count.
      status: 'active',
      // Stored so a later move can count 4 working days from the ORDER, not
      // from the day someone happens to reschedule it.
      orderDate: orderDate,
      // Digits-only copy of the phone number, so support can find this booking
      // from whatever form the customer reads out over the phone.
      telDigits: R.normalizeTel(booking.telephone),
      history: visitId
        ? [{ action: 'joinVisit', with: leader.soNo || '', by: booking.user || '', at: new Date().toISOString() }]
        : [],
      createdAt: new Date().toISOString()
    }))
  })

  return newRef.id
}

// A day's counters as they stand, for a path that moves none of them.
function countsOf (day) {
  const d = day || {}
  return { am: d.am || 0, pm: d.pm || 0, fullDay: d.fullDay || 0, total: d.total || 0, outOfArea: d.outOfArea || 0 }
}

// Write a trip and its mirror together, and hand back the fragment the day patch
// needs. Shared by every path that moves a seat, because a trip counted in one
// place and not the other is a route that offers a seat it has already sold --
// and there are now four such paths.
function applyTripDelta (t, dayDoc, tripRef, tripId, next) {
  t.update(tripRef, { count: next.count, status: next.status, autoClosed: next.autoClosed })
  return { tripByProvince: R.mirrorAfterTrip(dayDoc, tripId, next) }
}

// The reads and writes of a cancellation, minus the booking document itself:
// shared by cancelBooking and by confirming a cancel proposal, so the two can
// never charge the day differently. Reads first, then writes, as the Web SDK
// v1 requires; the caller has already read the booking and writes the
// returned patch onto it.
async function cancelWrites (t, b, { user, reason }) {
  const daySnap = await t.get(DAY_REF(b.date))
  const day = daySnap.exists ? daySnap.data() : Object.assign({}, EMPTY_COUNTS)

  // A member of a visit gives the seat back only as the last one out; until
  // then the visit still stops there. The route is then not read at all, since
  // it would not be written.
  const out = V.leave(day, b)
  const tripRef = b.tripId && out.releasesSeat ? TRIP_COL().doc(b.tripId) : null
  const tripSnap = tripRef ? await t.get(tripRef) : null
  const trip = tripSnap && tripSnap.exists ? tripSnap.data() : null

  const dayPatch = out.releasesSeat
    ? Object.assign({ date: b.date }, R.countsAfterRemove(day, b.slot, b.outOfArea))
    : { date: b.date }
  if (out.visits) dayPatch.visits = out.visits
  if (tripRef && trip) {
    // A route the system closed by filling reopens; one delivery closed by
    // hand stays closed. Cancelling the LAST booking does not close a route
    // either -- the truck stays reserved until delivery says otherwise, and a
    // route with nobody on it is where this workflow starts.
    Object.assign(dayPatch, applyTripDelta(t, day, tripRef, b.tripId, R.tripAfterRelease(trip)))
  } else if (tripRef) {
    // Read but not found. Trips are never deleted, so this is corrupt data --
    // and the Web SDK v1 would reject the whole transaction for the unmatched
    // read, leaving the booking impossible to cancel. An empty merge is a
    // legal write that changes nothing and lets the cancellation through.
    t.set(tripRef, {}, { merge: true })
  }
  t.set(DAY_REF(b.date), dayPatch, { merge: true })

  return {
    status: 'cancelled',
    cancelledAt: new Date().toISOString(),
    cancelledBy: user || '',
    // Cancellation joins the move log, so one field is the whole story of
    // what happened to this booking and who did it.
    history: (b.history || []).concat([{
      action: 'cancel',
      from: { date: b.date, slot: b.slot },
      reason: reason || '',
      by: user || '',
      at: new Date().toISOString()
    }])
  }
}

// Cancelling is the only path that returns a seat to the pool.
async function cancelBooking (bookingId, user, reason) {
  await db.runTransaction(async t => {
    const bRef = SLOT_COL().doc(bookingId)
    const bSnap = await t.get(bRef)
    if (!bSnap.exists) throw new R.BookingError('NOT_FOUND')

    const b = bSnap.data()
    refuseUpcountry(b)
    if (!R.isActive(b.status)) {
      // Idempotent: never decrement twice. A bare `return` here would commit a
      // transaction that read this document without writing it, which the Web
      // SDK v1 rejects outright -- so re-assert the terminal state instead. The
      // day document is deliberately not read on this path, since not reading
      // it means there is nothing extra to write.
      t.update(bRef, { status: 'cancelled' })
      return
    }
    // A pending proposal holds a seat on another day. Cancelling past it would
    // leave that seat held for a booking that no longer exists; deciding the
    // proposal first is one click on the panel.
    if (X.isPending(b)) throw new R.BookingError('PROPOSAL_PENDING')

    t.update(bRef, await cancelWrites(t, b, { user, reason }))
  })
}

// Moving a booking to another day or slot. Two tiers, by role:
//
//   the owner   -- may move the booking they created, but the new date must
//                  obey every rule a fresh booking obeys: a free seat, not a
//                  Sunday, and still 4 working days after the ORDER date.
//   STAFF_ROLES -- may move any booking anywhere, nothing enforced. The office
//                  needs that to absorb a breakdown or a customer emergency; a
//                  day pushed past its cap is flagged in the calendar exactly
//                  as a cut in the car count is.
//
// Counters for both days move inside the one transaction, so a move can never
// leave a seat charged to a day the booking has left.
async function moveBooking (bookingId, { date, slot, reason, note, user, role, owners }) {
  if (R.SLOTS.indexOf(slot) === -1) throw new R.BookingError('BAD_SLOT')
  if (!reason) throw new R.BookingError('NO_REASON')

  const cfg = await getConfig()
  const free = R.isStaffRole(role)
  const bRef = SLOT_COL().doc(bookingId)
  let result = { over: false }

  await db.runTransaction(async t => {
    const bSnap = await t.get(bRef)
    if (!bSnap.exists) throw new R.BookingError('NOT_FOUND')
    const b = bSnap.data()
    refuseUpcountry(b)

    if (!R.isActive(b.status)) throw new R.BookingError('ALREADY_CANCELLED')
    if (X.isPending(b)) throw new R.BookingError('PROPOSAL_PENDING')
    if (!free && !ownedBy(b.user, ownersOf({ owners, user }))) throw new R.BookingError('NOT_ALLOWED')
    if (b.date === date && b.slot === slot) throw new R.BookingError('SAME_SLOT')

    const sameDay = b.date === date
    // Every read happens before any write, and every document read here is also
    // written -- both are hard requirements of the Web SDK v1 transaction API.
    const oldSnap = await t.get(DAY_REF(b.date))
    const newSnap = sameDay ? oldSnap : await t.get(DAY_REF(date))

    const oldDay = oldSnap.exists ? oldSnap.data() : Object.assign({}, EMPTY_COUNTS)
    const newDay = newSnap.exists ? newSnap.data() : Object.assign({}, EMPTY_COUNTS)

    // Moving an out-of-area booking is moving it to another ROUTE. There is no
    // such thing as an out-of-area booking on a day with no route to its
    // province -- that is what a route IS.
    //
    // Which route it lands on has to be decided BEFORE the old one is read. A
    // same-day slot change -- เช้า -> บ่าย -- keeps the booking on the route it
    // is already on, so there is nothing to write to it, and the Web SDK v1
    // rejects a transaction that reads a document it does not write. attachSo
    // makes exactly this move for the day document and says so.
    const targetEntry = (newDay.tripByProvince || {})[R.provinceOf(b)]
    const toId = b.outOfArea ? (targetEntry && targetEntry.tripId) || null : null

    // A move always leaves the booking's visit: it arrives as an ordinary
    // booking, and whoever it shared a stop with stays behind. It gives its old
    // seat back only if it was the last of the visit there.
    const out = V.leave(oldDay, b)

    // Which routes change, as NET seats. A same-day slot change on the route it
    // is already on is 0 -- and that route is then neither read nor written,
    // which the Web SDK v1 requires (attachSo makes the same move for the day
    // document). A visit member that is not the last out gives nothing back, so
    // the same change costs it a seat of its own.
    const deltas = V.tripDeltas([
      { tripId: b.tripId, delta: out.releasesSeat ? -1 : 0 },
      { tripId: toId, delta: 1 }
    ])
    const tripIds = Object.keys(deltas)
    const tripSnaps = {}
    for (let i = 0; i < tripIds.length; i++) {
      tripSnaps[tripIds[i]] = await t.get(TRIP_COL().doc(tripIds[i]))
    }

    // The booking leaves its current day first. For a same-day slot change that
    // is the same document, so the target slot is tested against a day that no
    // longer counts this booking -- which is what lets a day sitting on its cap
    // accept an เช้า -> บ่าย swap it already holds a seat for.
    const freed = out.releasesSeat ? R.countsAfterRemove(oldDay, b.slot, b.outOfArea) : countsOf(oldDay)
    const basis = sameDay ? freed : newDay
    const caps = capsForDay(newDay, cfg.cars, cfg.perCar)

    if (!free) {
      const orderDate = b.orderDate || String(b.createdAt || '').slice(0, 10) || R.todayStr()
      if (R.isClosedSunday(date, newDay)) throw new R.BookingError('SUNDAY')
      // The live case for the check above: orderDate here is the booking's
      // own, so one made weeks ago measured TOO_EARLY against a date weeks
      // gone, and its owner could move it onto a day that has already passed.
      if (date < R.todayStr()) throw new R.BookingError('PAST')
      if (date < R.earliestDeliveryDate(orderDate, cfg.leadDays)) throw new R.BookingError('TOO_EARLY')
      if (date > R.horizonDate(R.todayStr(), cfg.horizonDays)) throw new R.BookingError('TOO_LATE')
      const open = R.checkDayOpen(newDay, role, { outOfArea: b.outOfArea, sameDay: sameDay })
      if (!open.ok) throw new R.BookingError(open.reason)
      // Same exemption as createBooking, and for the same reason. It can only
      // matter to a booking a shop owns, which today no out-of-area booking is
      // -- but the two paths agreeing is worth more than the case being rare.
      if (!b.outOfArea) {
        const check = R.checkCapacity(basis, caps, slot)
        if (!check.ok) throw new R.BookingError(check.reason)
      }
      // Staff keep the free-move power they have today, so a staff move may land
      // on a date whose route they are about to open. An owner may not.
      if (b.outOfArea) {
        if (!toId) throw new R.BookingError('NO_TRIP')
        if (deltas[toId] > 0) {
          const snap = tripSnaps[toId]
          const seat = R.checkTripSeat(snap && snap.exists ? snap.data() : null)
          if (!seat.ok) throw new R.BookingError(seat.reason)
        }
      }
    }

    const filled = R.countsAfterAdd(basis, slot, b.outOfArea)
    const fromPatch = {}
    const toPatch = {}
    if (out.visits) fromPatch.visits = out.visits
    tripIds.forEach(id => {
      const ref = TRIP_COL().doc(id)
      const snap = tripSnaps[id]
      if (!snap.exists) {
        // Read but not found. Trips are never deleted, so this is corrupt data
        // -- and the Web SDK v1 would reject the whole transaction for the
        // unmatched read, leaving the booking impossible to move. An empty merge
        // is a legal write that changes nothing and lets the move through.
        t.set(ref, {}, { merge: true })
        return
      }
      let next = snap.data()
      const d = deltas[id]
      for (let k = 0; k < Math.abs(d); k++) next = d > 0 ? R.tripAfterSeat(next) : R.tripAfterRelease(next)
      // The mirror lives on the route's own day: the target route on the new
      // day, the one being left on the old.
      const onNew = id === toId
      Object.assign(onNew ? toPatch : fromPatch, applyTripDelta(t, onNew ? newDay : oldDay, ref, id, next))
    })

    if (sameDay) {
      t.set(DAY_REF(date), Object.assign({ date: date }, filled, fromPatch, toPatch), { merge: true })
    } else {
      t.set(DAY_REF(b.date), Object.assign({ date: b.date }, freed, fromPatch), { merge: true })
      t.set(DAY_REF(date), Object.assign({ date: date }, filled, toPatch), { merge: true })
    }

    // Reported back so the page can say so out loud when a privileged move has
    // pushed a day past what its cars can carry.
    // Staff may move a booking anywhere, including onto a day with no route to
    // its province. That leaves it a stray -- real, dated, and needing a route
    // later. Reported rather than refused, exactly as an over-capacity move is.
    result = { over: filled.total > caps.total, total: filled.total, cap: caps.total,
      stranded: !!b.outOfArea && !toId }

    t.update(bRef, {
      date: date,
      slot: slot,
      tripId: toId,
      // Out of its visit, whatever happened to the rest of it.
      visitId: null,
      movedAt: new Date().toISOString(),
      movedBy: user || '',
      // Append-only: a move never overwrites what an earlier move recorded.
      history: (b.history || []).concat([{
        action: 'move',
        from: { date: b.date, slot: b.slot },
        to: { date: date, slot: slot },
        reason: reason,
        note: note || '',
        by: user || '',
        at: new Date().toISOString(),
        leftVisit: !!b.visitId
      }])
    })
  })

  return result
}

// A correction, not a reschedule. Customers ring back with a landmark they
// forgot, a pin they finally dropped, or one more item -- none of which touches
// capacity, so no bookingDay document is involved and none of the booking rules
// apply. Only EDIT_FIELDS can be reached: เลขที่ SO is the link to SAP and the
// duplicate-booking guard, and date/slot belong to moveBooking.
//
// Same permission as a move: the owner may correct their own booking, staff may
// correct any.
async function editBooking (bookingId, { fields, user, role, owners }) {
  const raw = (fields && fields.geo) || ''
  const geo = R.parseGeo(raw)
  // Refused rather than silently dropped: a half-parsed coordinate sends a
  // driver somewhere confidently wrong, which is worse than no coordinate.
  if (raw && !geo) throw new R.BookingError('BAD_GEO')
  // Stored canonically, exactly as createBooking stores it, so the next diff
  // does not see a spurious change.
  const clean = Object.assign({}, fields, { geo: geo ? R.formatGeo(geo) : '' })

  const bRef = SLOT_COL().doc(bookingId)
  let changes = []

  await db.runTransaction(async t => {
    const bSnap = await t.get(bRef)
    if (!bSnap.exists) throw new R.BookingError('NOT_FOUND')
    const b = bSnap.data()
    refuseUpcountry(b)

    if (!R.isActive(b.status)) throw new R.BookingError('ALREADY_CANCELLED')
    if (!R.isStaffRole(role) && !ownedBy(b.user, ownersOf({ owners, user }))) throw new R.BookingError('NOT_ALLOWED')

    changes = R.diffFields(b, clean)
    // Nothing to write means nothing to log. Without this, opening the dialog
    // and pressing save would stamp an empty edit into the history.
    if (!changes.length) throw new R.BookingError('NO_CHANGES')

    const patch = {
      editedAt: new Date().toISOString(),
      editedBy: user || '',
      // Every changed field, with what it was -- the record of an address that
      // changed the day before delivery is the whole point.
      history: (b.history || []).concat([{
        action: 'edit',
        changes: changes,
        by: user || '',
        at: new Date().toISOString()
      }])
    }
    changes.forEach(c => { patch[c.field] = c.to })
    t.update(bRef, patch)
  })

  return changes
}

// The พิกัด / จุดสังเกต typed onto a planned appointment in carBooking's slot
// dialog, copied back to the booking it was planned from.
//
// Not optional: commitPlan rebuilds every auto-assigned `booking` document from
// its bookingSlot, so a pin written only onto `booking` is erased by the next
// re-plan of that date.
//
// No owner check, unlike editBooking, and on purpose: the slot dialog lets every
// role that can open carBooking set a pin on any row it sees, shops included,
// because a shop that knows the customer saves delivery the call. The history
// entry records who did it. A booking that is gone or cancelled is left alone --
// it is no longer the source of anything.
async function setPin (bookingId, { geo, landmark, user }) {
  const raw = geo || ''
  const point = R.parseGeo(raw)
  if (raw && !point) throw new R.BookingError('BAD_GEO')
  const clean = { geo: point ? R.formatGeo(point) : '', landmark: String(landmark || '').trim() }

  const bRef = SLOT_COL().doc(bookingId)
  let changes = []

  try {
    await db.runTransaction(async t => {
      const bSnap = await t.get(bRef)
      // Nothing to copy to. Thrown rather than returned: the Web SDK v1 rejects a
      // transaction that reads a document it does not write, and a throw aborts
      // it without committing anything.
      if (!bSnap.exists) throw new R.BookingError('NOT_FOUND')
      const b = bSnap.data()
      if (!R.isActive(b.status)) throw new R.BookingError('ALREADY_CANCELLED')
      changes = R.diffFields(b, clean, ['geo', 'landmark'])
      if (!changes.length) throw new R.BookingError('NO_CHANGES')

      const patch = {
        editedAt: new Date().toISOString(),
        editedBy: user || '',
        history: (b.history || []).concat([{
          action: 'pin', changes: changes, by: user || '', at: new Date().toISOString()
        }])
      }
      changes.forEach(c => { patch[c.field] = c.to })
      t.update(bRef, patch)
    })
  } catch (e) {
    if (e instanceof R.BookingError &&
        ['NOT_FOUND', 'ALREADY_CANCELLED', 'NO_CHANGES'].indexOf(e.reason) !== -1) return []
    throw e
  }

  return changes
}

// Filling in the SO number a นัดหมาย was booked without.
//
// Delivery holds the day first and the SO lands one to three days before it, so
// everything on the booking until then was a stand-in: a name as the shop heard
// it, an address read out over the phone, a guess at the item count. Attaching
// the SO replaces all of it with what SAP says -- see mergeSoFields for the one
// exception, a field the SO carries nothing for.
//
// This is NOT a reschedule. No bookingDay document is read or written: the day
// and the slot the customer was promised do not move, and no seat changes
// hands. That is what lets the whole thing run without the capacity rules.
//
// Same two tiers as a move or a correction: staff may attach to any booking,
// everyone else only to one they created.
async function attachSo (bookingId, { soNo, so, orderDate, user, role, outOfArea, owners, profiles }) {
  const num = String(soNo || '').trim()
  if (!num) throw new R.BookingError('NO_SO')

  const raw = (so && so.geo) || ''
  const geo = R.parseGeo(raw)
  // Refused rather than silently dropped, exactly as editBooking refuses it: a
  // half-parsed coordinate sends a driver somewhere confidently wrong.
  if (raw && !geo) throw new R.BookingError('BAD_GEO')
  const clean = Object.assign({}, so, { geo: geo ? R.formatGeo(geo) : '' })

  // Read outside the transaction for the same reason getConfig is: the Web SDK
  // v1 requires every document read inside one to also be written in it, and
  // this is a query across the collection besides. The race it leaves -- two
  // people attaching the same SO to two bookings in the same second -- is the
  // one the duplicate check on the booking form already lives with.
  const existing = await findActiveBookingForSo(num)
  if (existing && existing.id !== bookingId) throw new R.BookingError('SO_TAKEN')

  const bRef = SLOT_COL().doc(bookingId)
  let changes = []

  await db.runTransaction(async t => {
    const bSnap = await t.get(bRef)
    if (!bSnap.exists) throw new R.BookingError('NOT_FOUND')
    const b = bSnap.data()
    refuseUpcountry(b)

    if (!R.isActive(b.status)) throw new R.BookingError('ALREADY_CANCELLED')
    // Who may: the owner, staff, or a supervisor of the owner's group
    // (X.canAttachSo). HAS_SO is its own refusal just below, so the rule is
    // asked as though the number were still blank.
    if (!X.canAttachSo(Object.assign({}, b, { soNo: '' }), { role, identities: ownersOf({ owners, user }), user, profiles })) {
      throw new R.BookingError('NOT_ALLOWED')
    }
    // One-way on purpose. Overwriting an SO number that is already there would
    // point the booking at a different job at a different address, and the SO it
    // left behind would silently become bookable again -- a correction with
    // consequences nobody asked this dialog for.
    if (b.soNo) throw new R.BookingError('HAS_SO')

    const merged = R.mergeSoFields(b, clean)
    // Over SO_DIFF_FIELDS only: what a person can read is what the dialog showed
    // them and what the history should say changed.
    changes = R.diffFields(b, merged, R.SO_DIFF_FIELDS)

    // The SO replaces `location`, so this is the one path that can move a
    // booking across the service-area line after it was made -- an appointment
    // taken over the phone as สมุทรสาคร whose SO turns out to be มหาชัย. Both
    // the flag and the day's counters have to follow it: leaving the flag would
    // hide the booking from the grouping view, and leaving the counters would
    // charge a far-province trip a seat it does not use (or free a seat an
    // in-area delivery does). Derived the same way createBooking derives it, so
    // the two cannot disagree.
    const area = locationArea(merged.location)
    const nowOut = area === 'unknown' ? !!outOfArea : area === 'out'
    const wasOut = !!b.outOfArea

    // A booking sharing a stop was linked because its phone and ตำบล matched.
    // An SO that says otherwise makes it a different destination on a seat it
    // does not hold -- and crossing the service-area line always changes the
    // ตำบล, so this also covers the one case below that moves counters.
    if (b.visitId && V.visitKey(merged) !== V.visitKey(b)) throw new R.BookingError('VISIT_LINKED')

    const patch = {
      soNo: num,
      soAttachedAt: new Date().toISOString(),
      soAttachedBy: user || '',
      history: (b.history || []).concat([{
        action: 'attachSo',
        soNo: num,
        changes: changes,
        by: user || '',
        at: new Date().toISOString()
      }])
    }
    // Recorded only when it moved, and recorded at all because it is the one
    // thing an attach changes that is not a field on the diff: the booking left
    // (or joined) the day's car capacity, and whoever reads this row later
    // should not have to work that out from the ที่อยู่ line.
    if (nowOut !== wasOut) patch.history[patch.history.length - 1].areaMovedTo = nowOut ? 'out' : 'in'
    // undefined is not a value Firestore will take, so a field neither the
    // booking nor the SO ever had is left unwritten rather than written empty.
    R.SO_FIELDS.forEach(f => {
      if (merged[f] !== undefined) patch[f] = merged[f]
    })
    // Re-derived, never carried: the phone number just came from SAP, and
    // telDigits is the only thing a support search matches on. Leaving the old
    // copy would make this booking unfindable by the number now printed on it.
    patch.telDigits = R.normalizeTel(merged.telephone)
    // The lead-time rule counts from the order, and until now the booking's
    // orderDate was the day the appointment was made. The SO's docDate is the
    // real one. It is only ever recorded, never re-checked: the customer already
    // has this date, and a rule applied after the fact would fail a booking
    // nobody can now change.
    if (orderDate) patch.orderDate = orderDate
    patch.outOfArea = nowOut

    // Only when it actually moved. The Web SDK v1 rejects a transaction that
    // reads a document without writing it, so an unchanged flag must not read
    // the day at all -- and the overwhelmingly common attach does not change it.
    if (nowOut !== wasOut) {
      // A pending move holds a seat charged as in-area or out-of-area; flipping
      // the flag under it would release the hold from the wrong counter.
      if (X.isPending(b)) throw new R.BookingError('PROPOSAL_PENDING')
      const daySnap = await t.get(DAY_REF(b.date))
      const day = daySnap.exists ? daySnap.data() : Object.assign({}, EMPTY_COUNTS)
      const moved = R.countsAfterAdd(R.countsAfterRemove(day, b.slot, wasOut), b.slot, nowOut)
      t.set(DAY_REF(b.date), Object.assign({ date: b.date }, moved), { merge: true })
    }

    t.update(bRef, patch)
  })

  return changes
}

// Two live bookings of one customer on one day and slot become one stop, by a
// person's decision (bookingDate's รวมจุดส่ง, or the ask when booking). `bookingId`
// joins `withId`'s visit and gives its own seat back -- on the day, and on the
// route when out of area. See visitRules.checkLink for what must match.
//
// Staff may link any two; anyone else only a booking they own, and the key
// check is what stops a seat being given away to a stranger's order.
async function linkVisit (bookingId, withId, { user, role, owners } = {}) {
  const bRef = SLOT_COL().doc(bookingId)
  const lRef = SLOT_COL().doc(withId)
  const stamp = new Date().toISOString()
  await db.runTransaction(async t => {
    const bSnap = await t.get(bRef)
    const lSnap = await t.get(lRef)
    if (!bSnap.exists || !lSnap.exists) throw new R.BookingError('NOT_FOUND')
    const b = bSnap.data()
    refuseUpcountry(b)
    const leader = lSnap.data()
    if (!R.isStaffRole(role) && !ownedBy(b.user, ownersOf({ owners, user }))) throw new R.BookingError('NOT_ALLOWED')
    const link = V.checkLink(b, bookingId, leader, withId)
    if (!link.ok) throw new R.BookingError(link.reason)
    const vid = link.visitId

    const daySnap = await t.get(DAY_REF(b.date))
    const day = daySnap.exists ? daySnap.data() : Object.assign({}, EMPTY_COUNTS)
    const tripRef = b.outOfArea && b.tripId ? TRIP_COL().doc(b.tripId) : null
    const tripSnap = tripRef ? await t.get(tripRef) : null

    // Both already hold a seat as ordinary bookings. The leader becomes the
    // visit's first member if it was not one, and `b` joins it -- so the day
    // counts the visit's members and gives `b`'s seat back.
    // At least one: the leader is live here (checkLink), so zero is drift.
    const base = leader.visitId ? Math.max(1, V.membersOn(day, vid)) : 1
    const dayPatch = Object.assign({ date: b.date }, R.countsAfterRemove(day, b.slot, b.outOfArea),
      { visits: { [vid]: base + 1 } })
    if (tripRef && tripSnap.exists) {
      Object.assign(dayPatch, applyTripDelta(t, day, tripRef, b.tripId, R.tripAfterRelease(tripSnap.data())))
    } else if (tripRef) {
      t.set(tripRef, {}, { merge: true })
    }
    t.set(DAY_REF(b.date), dayPatch, { merge: true })

    t.update(lRef, leader.visitId ? { visitId: vid } : {
      visitId: vid,
      history: (leader.history || []).concat([{ action: 'linkVisit', with: b.soNo || '', by: user || '', at: stamp }])
    })
    t.update(bRef, {
      visitId: vid,
      history: (b.history || []).concat([{ action: 'joinVisit', with: leader.soNo || '', by: user || '', at: stamp }])
    })
  })
}

// The undo: `bookingId` becomes its own stop again. If others of the visit are
// still on the day it needs a seat of its own, which a shop only gets if there
// is one (checkCapacity / checkTripSeat); staff keep their free-move power.
// If it was the last member left -- the others cancelled or moved -- it already
// holds the visit's seat and only the link is cleared.
async function unlinkVisit (bookingId, { user, role, owners } = {}) {
  const cfg = await getConfig()
  const free = R.isStaffRole(role)
  const bRef = SLOT_COL().doc(bookingId)
  let result = { over: false }
  const stamp = new Date().toISOString()
  await db.runTransaction(async t => {
    const bSnap = await t.get(bRef)
    if (!bSnap.exists) throw new R.BookingError('NOT_FOUND')
    const b = bSnap.data()
    refuseUpcountry(b)
    if (!R.isActive(b.status)) throw new R.BookingError('ALREADY_CANCELLED')
    if (!b.visitId) throw new R.BookingError('NOT_IN_VISIT')
    if (X.isPending(b)) throw new R.BookingError('PROPOSAL_PENDING')
    if (!free && !ownedBy(b.user, ownersOf({ owners, user }))) throw new R.BookingError('NOT_ALLOWED')

    const daySnap = await t.get(DAY_REF(b.date))
    const day = daySnap.exists ? daySnap.data() : Object.assign({}, EMPTY_COUNTS)
    const out = V.leave(day, b)
    // Others remain: this booking now needs its own seat.
    const needsSeat = !out.releasesSeat
    const tripRef = needsSeat && b.outOfArea && b.tripId ? TRIP_COL().doc(b.tripId) : null
    const tripSnap = tripRef ? await t.get(tripRef) : null
    const trip = tripSnap && tripSnap.exists ? tripSnap.data() : null

    const dayPatch = { date: b.date, visits: out.visits }
    if (needsSeat) {
      if (!free) {
        if (b.outOfArea) {
          const seat = R.checkTripSeat(trip)
          if (!seat.ok) throw new R.BookingError(seat.reason)
        } else {
          const check = R.checkCapacity(day, capsForDay(day, cfg.cars, cfg.perCar), b.slot)
          if (!check.ok) throw new R.BookingError(check.reason)
        }
      }
      const filled = R.countsAfterAdd(day, b.slot, b.outOfArea)
      Object.assign(dayPatch, filled)
      result = { over: filled.total > capsForDay(day, cfg.cars, cfg.perCar).total }
      if (tripRef && trip) Object.assign(dayPatch, applyTripDelta(t, day, tripRef, b.tripId, R.tripAfterSeat(trip)))
      else if (tripRef) t.set(tripRef, {}, { merge: true })
    }
    t.set(DAY_REF(b.date), dayPatch, { merge: true })
    t.update(bRef, {
      visitId: null,
      history: (b.history || []).concat([{ action: 'unlinkVisit', by: user || '', at: stamp }])
    })
  })
  return result
}

// Live bookings on `date` that `booking` could share a stop with, for the ask
// when booking. A query, so outside any transaction; createBooking checks the
// chosen one again inside its own.
async function findVisitCandidates (booking) {
  if (!booking || !booking.date || !V.visitKey(booking)) return []
  const snap = await SLOT_COL().where('date', '==', booking.date).get()
  const rows = []
  snap.forEach(d => rows.push(Object.assign({ id: d.id }, d.data())))
  return V.linkCandidates(Object.assign({ status: 'active' }, booking), rows)
}

// The ASMX GetSo check cannot see bookings made here (this flow never sets
// transNo), so an already-booked SO would otherwise validate clean and consume
// a second seat.
async function findActiveBookingForSo (soNo) {
  const snap = await SLOT_COL().where('soNo', '==', String(soNo || '').trim()).get()
  let found = null
  snap.forEach(d => {
    const b = d.data()
    if (!found && R.isActive(b.status)) found = Object.assign({ id: d.id }, b)
  })
  return found
}

// The same guard for an RT: one return, one live booking. An exchange holds its
// RT too, so an RT already riding on an SO's booking is found here.
async function findActiveBookingForRt (rtNo) {
  const snap = await SLOT_COL().where('rtNo', '==', String(rtNo || '').trim()).get()
  let found = null
  snap.forEach(d => {
    const b = d.data()
    if (!found && R.isActive(b.status)) found = Object.assign({ id: d.id }, b)
  })
  return found
}

// เพิ่ม RT ในการจองนี้: an SO booking becomes an exchange. No seat moves, so no
// bookingDay is read or written (the Web SDK v1 rule) -- the planner sees the
// extra 30 minutes the next time it plans the day.
async function attachRt (bookingId, { fields, user, role, owners, profiles }) {
  const rtNo = String((fields && fields.rtNo) || '').trim()
  if (!rtNo) throw new R.BookingError('NOT_FOUND')
  const existing = await findActiveBookingForRt(rtNo)
  if (existing && existing.id !== bookingId) throw new R.BookingError('RT_TAKEN')

  const bRef = SLOT_COL().doc(bookingId)
  await db.runTransaction(async t => {
    const bSnap = await t.get(bRef)
    if (!bSnap.exists) throw new R.BookingError('NOT_FOUND')
    const b = bSnap.data()
    refuseUpcountry(b)
    const mayAttach = X.canAttachRt(b, { role, identities: ownersOf({ owners, user }), user, profiles })
    const check = RJ.checkAttachRt(b, { mayAttach })
    if (!check.ok) throw new R.BookingError(check.reason)
    t.update(bRef, {
      rtNo: rtNo,
      rtDocEntry: fields.rtDocEntry || null,
      jobType: 'exchange',
      prodListRt: fields.prodListRt || [],
      rtItemCount: fields.rtItemCount || 0,
      history: (b.history || []).concat([{ action: 'attachRt', rtNo: rtNo, by: user || '', at: new Date().toISOString() }])
    })
  })
}

// One booking by id, or null. For ค้นหาการจอง's ใส่ SO link
// (bookingDate?attachSo=<id>), which names the booking rather than a search.
async function getBooking (bookingId) {
  if (!bookingId) return null
  const snap = await SLOT_COL().doc(String(bookingId)).get()
  return snap.exists ? Object.assign({ id: snap.id }, snap.data()) : null
}

// Every live นัดหมาย still waiting for its SO, for a group supervisor's
// นัดหมายรอ SO list. A นัดหมาย is created with `soNo: null` and attachSo is the
// only thing that fills it, so an equality filter on null finds exactly these
// -- an automatic single-field index, no composite one. Checked against the
// live project on 2026-09-28: 59 documents, 37 of them active. Cancelled rows
// and ones the caller may not attach to are dropped by the caller's filter.
function subscribeAwaitingSo (cb, onError) {
  return SLOT_COL()
    .where('soNo', '==', null)
    .onSnapshot(snap => {
      const rows = []
      snap.forEach(d => {
        const b = d.data()
        if (R.isActive(b.status) && RJ.isAppointment(b)) rows.push(Object.assign({ id: d.id }, b))
      })
      rows.sort(R.compareForDispatch)
      cb(rows)
    }, onError)
}

// Customer support: a caller reads out a phone number or an SO, and staff need
// the booking in one step. Both are single equality filters, which Firestore
// indexes automatically -- pairing either with a date range or a status filter
// would need a composite index, and an undeployed index fails the query
// outright, which is how this page first shipped showing a connection error.
//
// `owners`, when set, restricts the result to bookings made under any of those
// emails -- the account's current and previous ones. It is applied client-side
// because two equality filters would, again, need a composite index; these
// result sets are a handful of rows.
async function searchBookings ({ tel, soNo, rtNo, owners }) {
  const digits = R.normalizeTel(tel)
  const so = String(soNo || '').trim()
  const rt = String(rtNo || '').trim()
  let snap
  if (digits) snap = await SLOT_COL().where('telDigits', '==', digits).get()
  else if (so) snap = await SLOT_COL().where('soNo', '==', so).get()
  else if (rt) snap = await SLOT_COL().where('rtNo', '==', rt).get()
  else return []

  const rows = []
  snap.forEach(d => rows.push(Object.assign({ id: d.id }, d.data())))
  const visible = owners ? rows.filter(b => ownedBy(b.user, owners)) : rows
  // Latest date first: a caller asking "when is my delivery" means the one
  // still ahead of them, not the one from last year.
  visible.sort((a, b) => String(b.date).localeCompare(String(a.date)))
  return visible
}

// The working list that replaced รอยืนยัน when the confirm step was removed.
// Range filter on `date` alone (automatic index); cancelled rows and, for a
// shop login, other people's bookings are dropped client-side.
//
// Named for the range and not for "upcoming", because the page now points it at
// one chosen day -- which staff are free to put in the past to see what
// yesterday held. `from === to` is the ordinary case, not a special one.
function subscribeRange (from, to, owners, cb, onError) {
  return SLOT_COL()
    .where('date', '>=', from)
    .where('date', '<=', to)
    .onSnapshot(snap => {
      const rows = []
      snap.forEach(d => {
        const b = d.data()
        if (U.isUpcountry(b)) return
        if (!R.isActive(b.status)) return
        if (owners && !ownedBy(b.user, owners)) return
        rows.push(Object.assign({ id: d.id }, b))
      })
      // Sorting by date alone left a single day's rows in whatever order the
      // snapshot arrived in, which is not stable between snapshots of the same
      // day. compareForDispatch breaks that tie by the order the day is worked.
      rows.sort(R.compareForDispatch)
      cb(rows)
    }, onError)
}

async function commitChunked (writes) {
  for (let i = 0; i < writes.length; i += BATCH_LIMIT) {
    const batch = db.batch()
    writes.slice(i, i + BATCH_LIMIT).forEach(w => batch.set(w.ref, w.data, { merge: true }))
    await batch.commit()
  }
}

// A crashed vehicle is off the road for days or weeks, so capacity is edited
// over a range. Only the cap fields are written (merge: true) -- counts are
// never clobbered.
async function setCapacityRange ({ from, to, cars, note, user }) {
  // An opened Sunday is a working day and takes the new count; any other
  // Sunday is still skipped, so no override is written for a day off.
  const existing = await daysByDate(R.datesInRange(from, to, { skipSundays: false }).filter(R.isSunday))
  const dates = R.capacityDates(from, to, existing)
  const stamp = new Date().toISOString()
  // The standing rate is SNAPSHOT onto each day rather than asked for again.
  // Staff set the standard once; every range saved afterwards keeps the rate it
  // was saved under, so changing the standard later does not silently rewrite
  // days that were already planned.
  const standing = (await getConfig()).perCar
  await commitChunked(dates.map(date => ({
    ref: DAY_REF(date),
    data: {
      date,
      cars: Number(cars),
      perCar: standing,
      capNote: note || '',
      capUpdatedBy: user || '',
      capUpdatedAt: stamp
    }
  })))

  // Report which days are now over capacity. Reducing below existing bookings
  // is allowed on purpose; staff move or cancel those customers by hand.
  const cfg = await getConfig()
  const overCapacity = []
  for (let i = 0; i < dates.length; i += 10) {
    const chunk = dates.slice(i, i + 10)
    const snap = await db.collection('bookingDay').where('date', 'in', chunk).get()
    snap.forEach(d => {
      const day = d.data()
      const cap = capsForDay(day, cfg.cars, cfg.perCar)
      if ((day.total || 0) > cap.total) overCapacity.push({ date: d.id, total: day.total || 0, cap: cap.total })
    })
  }
  overCapacity.sort((a, b) => a.date.localeCompare(b.date))
  return { written: dates.length, overCapacity }
}

// ปิดรับจอง / เปิดรับจอง for one day. A merge of the stop fields alone -- no
// transaction, because no counter moves and nothing is decided from them here;
// createBooking and moveBooking read the flag inside their own transactions.
// Reopening keeps stoppedBy/stoppedAt beside reopenedBy/reopenedAt, so the day
// still says who closed it last.
async function setDayStopped (date, { stopped, note, user } = {}) {
  if (stopped && R.stopNoteRequired(date) && !String(note || '').trim()) throw new R.BookingError('STOP_NOTE_REQUIRED')
  const stamp = new Date().toISOString()
  const data = stopped
    ? { date: date, stopped: true, stopNote: String(note || '').trim(), stoppedBy: user || '', stoppedAt: stamp }
    : { date: date, stopped: false, reopenedBy: user || '', reopenedAt: stamp }
  await DAY_REF(date).set(data, { merge: true })
}

// เปิดรับวันอาทิตย์: delivery opens one Sunday as an extra working day, with
// the cars it will run. The cars are written like the จำนวนรถ editor writes
// them -- a count and a snapshot of the standing rate -- so the day's caps mean
// the same as on any weekday. There is no way back to a plain Sunday: closing
// is ปิดรับจอง with a reason (setDayStopped), which keeps staff able to add and
// move bookings there and leaves the bookings already on it going out.
// Reopening an opened-then-stopped Sunday is likewise setDayStopped.
async function setSundayOpen (date, { cars, note, user } = {}) {
  const ok = R.checkSundayOpen(date, { cars, note })
  if (!ok.ok) throw new R.BookingError(ok.reason)
  const standing = (await getConfig()).perCar
  const stamp = new Date().toISOString()
  await DAY_REF(date).set({
    date: date,
    sundayOpen: true,
    sundayNote: ok.note,
    sundayOpenedBy: user || '',
    sundayOpenedAt: stamp,
    cars: ok.cars,
    perCar: standing,
    capUpdatedBy: user || '',
    capUpdatedAt: stamp
  }, { merge: true })
}

// bookingDay documents for a list of dates, ten to a query ('in' allows ten).
async function daysByDate (dates) {
  const out = {}
  for (let i = 0; i < dates.length; i += 10) {
    const snap = await db.collection('bookingDay').where('date', 'in', dates.slice(i, i + 10)).get()
    snap.forEach(d => { out[d.id] = d.data() })
  }
  return out
}

async function setStandingConfig ({ cars, perCar, leadDays, horizonDays, user }) {
  await CONFIG_REF().set({
    cars: Number(cars),
    leadDays: R.normalizeLeadDays(leadDays),
    // Normalized on the way in as well as on the way out: this is the write the
    // TOO_LATE gate above will be reading, and the rules let anything reach it.
    horizonDays: R.normalizeHorizonDays(horizonDays),
    // Derived, not taken: the daily total is am + pm by construction, so a
    // caller cannot store three numbers that disagree.
    perCar: R.withDerivedTotal(perCar),
    updatedBy: user || '',
    updatedAt: new Date().toISOString()
  }, { merge: true })
}

// Counters can drift: a browser killed mid-write, or a direct Firestore edit,
// which the open rules permit. Without this, one drifted counter silently
// blocks a day forever.
async function recountRange (from, to) {
  // Range filter on `date` only, with the status test applied client-side:
  // pairing `status in [...]` with a date range would need a composite index,
  // and requiring a deployed index to run a repair tool is backwards -- the
  // repair must work on a project that has had nothing deployed to it.
  const snap = await SLOT_COL()
    .where('date', '>=', from)
    .where('date', '<=', to)
    .get()

  // Seed every date in range with zeros so a day whose bookings were all
  // cancelled gets its stale counter reset rather than left untouched.
  const tally = {}
  R.datesInRange(from, to, { skipSundays: false }).forEach(d => {
    tally[d] = { am: 0, pm: 0, fullDay: 0, total: 0 }
  })
  const add = e => {
    if (!tally[e.date]) tally[e.date] = { am: 0, pm: 0, fullDay: 0, total: 0 }
    if (e.slot === 'am') tally[e.date].am++
    else if (e.slot === 'pm') tally[e.date].pm++
    else if (e.slot === 'full') tally[e.date].fullDay++
    tally[e.date].total++
  }
  // Each booking's own day, gathered per date so a visit's members can share
  // one seat (V.recountDay), which also rebuilds the `visits` mirror.
  const own = {}
  snap.forEach(doc => {
    // A cancelled booking has already returned its seat, and a pending move
    // proposal holds a second one -- both decided by countsForRecount, so a
    // recount can never hand a held seat to someone else. Applied here rather
    // than in the query, per the note above. The slot tally below is the same
    // one this function has always kept.
    const b = doc.data()
    if (U.isUpcountry(b)) return
    X.countsForRecount(b).forEach((e, i) => {
      // Entry 0 is the booking's own day, the only one a visit applies to; a
      // held move target is an ordinary seat (a move leaves the visit).
      if (i === 0) (own[e.date] = own[e.date] || []).push(Object.assign({ visitId: b.visitId || null }, e))
      else add(e)
    })
  })
  const visits = {}
  Object.keys(own).forEach(date => {
    const day = V.recountDay(own[date])
    day.seated.forEach(add)
    visits[date] = day.visits
  })

  // A pending move INTO the range from a booking dated outside it: the range
  // query never saw that booking, but its hold sits on one of these days.
  // Equality on proposalStatus -- automatic index, a handful of rows.
  const open = await SLOT_COL().where('proposalStatus', '==', X.PENDING).get()
  open.forEach(doc => {
    const b = doc.data()
    if (b.date >= from && b.date <= to) return // already counted above
    X.countsForRecount(b).slice(1).forEach(e => {
      if (!tally[e.date]) return // outside the range
      add(e)
    })
  })

  const dates = R.datesInRange(from, to, { skipSundays: false })
  const existing = {}
  for (let i = 0; i < dates.length; i += 10) {
    const chunk = dates.slice(i, i + 10)
    const cur = await db.collection('bookingDay').where('date', 'in', chunk).get()
    cur.forEach(d => { existing[d.id] = d.data() })
  }

  const changed = []
  const writes = []
  dates.forEach(date => {
    const before = existing[date] || EMPTY_COUNTS
    const after = tally[date]
    // The mirror, with every stale key zeroed: the write is a merge, which
    // deep-merges maps, so a key left out would survive untouched.
    const mirror = Object.assign({}, visits[date] || {})
    Object.keys(before.visits || {}).forEach(k => { if (!(k in mirror)) mirror[k] = 0 })
    const mirrorDiffers = Object.keys(mirror).some(k => V.membersOn(before, k) !== mirror[k])
    const differs = ['am', 'pm', 'fullDay', 'total'].some(k => (before[k] || 0) !== after[k])
    // Skip days that have neither a document nor any bookings: writing zeros
    // would create thousands of empty documents.
    if ((!differs && !mirrorDiffers) || (!existing[date] && after.total === 0 && !mirrorDiffers)) return
    changed.push({ date, before: { am: before.am || 0, pm: before.pm || 0, fullDay: before.fullDay || 0, total: before.total || 0 }, after })
    const data = Object.assign({ date }, after)
    if (Object.keys(mirror).length) data.visits = mirror
    writes.push({ ref: DAY_REF(date), data: data })
  })

  await commitChunked(writes)
  return { days: dates.length, changed }
}

// Bookings written before the confirm step was removed have no telDigits, so a
// phone search cannot find them, and no orderDate, so an owner move has nothing
// to count lead time from. Both are derivable from what the record already
// holds. Runs alongside the recount, over the same range.
async function backfillSearchFields (from, to) {
  const snap = await SLOT_COL()
    .where('date', '>=', from)
    .where('date', '<=', to)
    .get()

  const writes = []
  snap.forEach(doc => {
    const b = doc.data()
    const patch = {}
    if (b.telDigits == null) patch.telDigits = R.normalizeTel(b.telephone)
    if (b.orderDate == null) patch.orderDate = String(b.createdAt || '').slice(0, 10) || b.date
    // 'pending' and 'confirmed' already count as active, so rewriting them
    // changes no behaviour -- it just stops the retired vocabulary spreading
    // into a UI that no longer has a confirm step.
    if (b.status === 'pending' || b.status === 'confirmed') patch.status = 'active'
    if (Object.keys(patch).length) writes.push({ ref: SLOT_COL().doc(doc.id), data: patch })
  })

  await commitChunked(writes)
  return { scanned: snap.size, patched: writes.length }
}

module.exports = {
  getConfig, subscribeMonth, capsForDay, createBooking, EMPTY_COUNTS, cancelBooking,
  moveBooking, editBooking, setPin, attachSo, findActiveBookingForSo, findActiveBookingForRt, attachRt, getBooking, subscribeAwaitingSo, searchBookings, subscribeRange,
  setCapacityRange, setDayStopped, setSundayOpen, setStandingConfig, recountRange, backfillSearchFields,
  openTrip, subscribeTrips, closeTrip, reopenTrip, cancelTrip, setTripCapacity, attachToTrip,
  addTripProvinces, removeTripProvince, linkVisit, unlinkVisit, findVisitCandidates,
  // For proposalService: the same seat arithmetic, not a second copy of it.
  applyTripDelta, cancelWrites
}
